import React, { useState } from 'react';
import cn from 'classnames';
import DatePicker, {
	ReactDatePickerCustomHeaderProps,
	registerLocale,
} from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';

import { getMonth, getYear } from 'utils/date';
import { ReactComponent as CalendarIcon } from 'assets/imgs/calendar.svg';
import { ReactComponent as CaretLeftIcon } from 'assets/imgs/caret-left.svg';
import { ReactComponent as CaretRightIcon } from 'assets/imgs/caret-right.svg';
import Input from '../';
import './datepicker.scss';
import classes from './inputDate.module.scss';
import { IInputDate, IInputDateProps, ViewTypes } from './types';

registerLocale('ru', ru);

const DEFAULT_VALUE = 'Весь';

const InputDate: React.FC<IInputDate> = ({
	className,
	startDate,
	endDate,
	onChange,
	placeholder,
	error,
}) => {
	const [view, setView] = useState<ViewTypes>('days');
	const startValue = startDate ? `${startDate.getTime()}` : '';
	const endValue = endDate ? `${endDate.getTime()}` : '';
	const inputValue = startDate
		? `${startValue}${endDate ? `-${endValue}` : ''}`
		: DEFAULT_VALUE;

	const handleChange = (timestamps: string): void => {
		const [startTimestamp, endTimestamp] = timestamps.split('-');
		const selectedStartDate = startTimestamp
			? new Date(Number(startTimestamp))
			: null;
		const selectedEndDate = endTimestamp
			? new Date(Number(endTimestamp))
			: null;
		onChange([selectedStartDate, selectedEndDate]);
	};

	const handleSelectView = (type: ViewTypes) => () => {
		setView(type);
	};

	const renderCustomHeader = ({
		date,
		decreaseMonth,
		increaseMonth,
		prevMonthButtonDisabled,
		nextMonthButtonDisabled,
	}: ReactDatePickerCustomHeaderProps) => (
		<div
			className={cn(classes.header, {
				[classes.header__days]: view === 'days',
			})}
		>
			{view === 'days' && (
				<button
					className={classes.prevButton}
					onClick={decreaseMonth}
					disabled={prevMonthButtonDisabled}
				>
					<CaretLeftIcon />
				</button>
			)}
			<div
				className={cn(classes.monthButton, {
					[classes.monthButton__active]: view === 'months',
				})}
				onClick={handleSelectView('months')}
			>
				{getMonth(date)}
			</div>
			<div
				className={cn(classes.yearButton, {
					[classes.yearButton__active]: view === 'years',
				})}
				onClick={handleSelectView('years')}
			>
				{getYear(date)}
			</div>
			{view === 'days' && (
				<button
					className={classes.nextButton}
					onClick={increaseMonth}
					disabled={nextMonthButtonDisabled}
				>
					<CaretRightIcon />
				</button>
			)}
		</div>
	);

	const renderInput = ({
		value: timestamps,
		onChange: onInputChange,
		dateView,
		...resProps
	}: IInputDateProps) => {
		const isDefault = timestamps === DEFAULT_VALUE;
		const [startTimestamp, endTimestamp] = timestamps.split('-');
		const selectedStartDate =
			startTimestamp && !isDefault
				? new Date(Number(startTimestamp))
				: new Date();
		const selectedEndDate = endTimestamp
			? new Date(Number(endTimestamp))
			: null;

		const handleDatePickerChange = (
			dates: [Date | null, Date | null]
		): void => {
			if (dateView === 'years') {
				setView('months');
			}
			if (dateView === 'months') {
				setView('days');
			}
			const [start, end] = dates;
			const timestamps = start
				? `${start.getTime()}${end ? `-${end.getTime()}` : ''}`
				: '';
			onInputChange({
				target: { value: timestamps },
			} as React.ChangeEvent<HTMLInputElement>);
		};

		return (
			<DatePicker
				calendarClassName={classes.calendar}
				popperClassName={classes.calendarWrapper}
				selected={selectedStartDate}
				startDate={selectedStartDate}
				endDate={selectedEndDate}
				value={isDefault ? 'Весь' : undefined}
				onChange={handleDatePickerChange}
				renderCustomHeader={renderCustomHeader}
				showPopperArrow={false}
				locale="ru"
				dateFormat="dd.MM.yyyy"
				selectsRange
				showMonthYearPicker={dateView === 'months'}
				showFullMonthYearPicker={dateView === 'months'}
				showYearPicker={dateView === 'years'}
				{...resProps}
			/>
		);
	};

	return (
		<Input
			className={cn(classes.inputDate, className)}
			value={inputValue}
			onChange={handleChange}
			placeholder={placeholder}
			error={error}
			renderInput={renderInput}
			endIcon={<CalendarIcon />}
			dateView={view}
		/>
	);
};

export default InputDate;
